import { useField } from 'formik';
import { useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Paragraph, QuillText, tokens } from '@treams/treams-ui';

import Tooltip from '../../../../packages/kakapo-ui-kit/components/tooltips/Tooltip';

const CompetencyLevelContainer = styled.div<{ selected: boolean }>`
	border-radius: 14px;
	width: 28px;
	height: 28px;
	flex-shrink: 0;
	flex-grow: 0;
	background: ${(props) => (props.selected ? props.theme.colors.primary : 'transparent')};
	padding: 8px;
	cursor: pointer;
	border: 1px solid
		${(props) => (props.selected ? props.theme.colors.primary : tokens.border.subtle)};
`;

const CompetencyDot = styled.div<{ selected: boolean }>`
	width: 10px;
	height: 10px;
	border-radius: 5px;
	background: ${(props) => (props.selected ? props.theme.colors.white : tokens.border.subtle)};
`;

function CompetencyLevel(props: {
	level: graphql.CompetencyLevelOption;
	fill: boolean;
	dot: boolean;
	selected: boolean;
	onClick: () => void;
}) {
	const { localize } = useLocalize();

	const [hover, setHover] = useState(false);

	return (
		<Box marginLeft={1} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
			{hover && (
				<Tooltip title={localize(props.level.level.title)?.text}>
					<QuillText value={localize(props.level.criteria ?? [])?.text} />
				</Tooltip>
			)}
			<CompetencyLevelContainer selected={props.fill} onClick={props.onClick}>
				{props.dot && <CompetencyDot selected={props.selected} />}
			</CompetencyLevelContainer>
		</Box>
	);
}

export default function Competency(props: {
	name: string;
	item: graphql.EvaluationCompetencyItem;
	readonly?: boolean;
}) {
	const { localize } = useLocalize();

	const [field, , { setValue }] = useField<number>(props.name + '.competency');
	const value = field.value ?? -1;

	return (
		<Flex alignItems="center">
			<Box flexGrow={1} style={{ maxWidth: 600 }}>
				<Paragraph>{localize(props.item.competency.title).text}</Paragraph>
			</Box>
			<Box flexGrow={1} />
			<Flex alignItems="center" flexGrow={0} flexShrink={0}>
				{props.item.competency.levels.map((level, index) => (
					<CompetencyLevel
						key={index}
						level={level}
						fill={level.enabled && value >= index}
						dot={value <= index || !level.enabled}
						selected={level.enabled && value === index}
						onClick={() => !props.readonly && level.enabled && setValue(index)}
					/>
				))}
			</Flex>
		</Flex>
	);
}
